import { z } from "zod";

import type { AgentCapabilities } from "@phoenix/agent/extensions/capabilities";
import type { AgentStore } from "@phoenix/store/agentStore";

import {
  getMountedUIOperationHandler,
  getUIOperationDescriptor,
  suggestUIOperationNames,
} from "./catalog";
import type { UIOperationCallContext, UIOperationResult } from "./types";

/** Everything dispatch needs from the enclosing `execute_browser_action` tool call. */
export type UIOperationDispatchContext = {
  agentStore: AgentStore;
  sessionId: string | null;
  capabilities: AgentCapabilities;
};

/**
 * Execute one operation call on behalf of a running `execute_browser_action` script.
 *
 * This is the single choke point every scripted effect flows through:
 * catalog lookup → capability gate → session gate → mounted check → schema
 * validation → handler invocation. It is the relocated core of the retired
 * `defineClientActionTool` execute path, run once per `ui.*` call instead of
 * once per tool call.
 *
 * Every failure mode returns an actionable `{ ok: false }` result (never
 * throws), so the calling script can branch on errors and the model can
 * recover — e.g. by calling `search_browser_actions` after an unknown-operation error.
 */
export async function dispatchUIOperationCall({
  operationName,
  input,
  callId,
  hostToolCallId,
  agentStore,
  sessionId,
  capabilities,
}: {
  operationName: string;
  input: unknown;
  /**
   * Unique id for this invocation, `<executeBrowserActionToolCallId>:<sequence>`.
   * Approval handlers key their pending-approval entries by it, and
   * interrupt cleanup cancels pending entries by tool-call-id prefix.
   */
  callId: string;
  /**
   * The enclosing `execute_browser_action` tool-call id — the chat card that hosts any
   * approval this call stages. Dispatch requests that card open when a
   * user-facing approval is about to appear.
   */
  hostToolCallId: string;
} & UIOperationDispatchContext): Promise<UIOperationResult> {
  const descriptor = getUIOperationDescriptor(operationName);
  if (descriptor == null) {
    const suggestions = suggestUIOperationNames(operationName).join(", ");
    return {
      ok: false,
      code: "UNKNOWN_OPERATION",
      error:
        `Unknown operation "${operationName}". Did you mean: ${suggestions}? ` +
        "Use search_browser_actions to discover operations and their signatures.",
    };
  }

  const missingCapability = (descriptor.requiredCapabilities ?? []).find(
    (capability) => !capabilities[capability]
  );
  if (missingCapability != null) {
    return {
      ok: false,
      code: "CAPABILITY_DISABLED",
      error: `Operation "${operationName}" requires the disabled capability "${missingCapability}".`,
    };
  }

  if (descriptor.requireSession && sessionId == null) {
    return {
      ok: false,
      code: "NO_SESSION",
      error: `Operation "${operationName}" requires an active session.`,
    };
  }

  const handler = getMountedUIOperationHandler(agentStore, operationName);
  if (handler == null) {
    const routeHint = descriptor.availability?.routeHint;
    return {
      ok: false,
      code: "NOT_AVAILABLE",
      error:
        `Operation "${operationName}" is not available on the current page` +
        (routeHint ? `; it requires ${routeHint}.` : ".") +
        " Use ui.navigation.goTo({ path, reason }) to ask the user to go" +
        " there, then retry.",
    };
  }

  const parsed = descriptor.inputSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      code: "INVALID_INPUT",
      error: `Invalid input for "${operationName}": ${z.prettifyError(parsed.error)}`,
    };
  }

  const context: UIOperationCallContext = { callId, sessionId };

  // A user-facing approval card is about to be staged inside the host
  // execute_browser_action card: request it open so Accept/Reject is never hidden behind
  // a collapsed disclosure, and release the request once the user decides so
  // the card collapses again when nothing awaits them. This is the single
  // choke point every approval operation flows through, so new approval
  // operations inherit the behavior from their `kind` — no per-operation
  // wiring. In bypass edit mode most approvals auto-accept without a card,
  // so nothing opens unless the operation always asks
  // (`alwaysRequiresApproval`).
  const opensHostCard =
    descriptor.operationKind === "approval" &&
    (descriptor.alwaysRequiresApproval === true ||
      agentStore.getState().permissions.edits === "manual");
  if (opensHostCard) {
    agentStore.getState().requestToolPartOpen(hostToolCallId);
  }

  try {
    const result = await handler(parsed.data, context);
    if (result.ok && result.output == null) {
      return { ok: true, output: descriptor.defaultSuccessOutput ?? "Done." };
    }
    return result;
  } catch (error) {
    return {
      ok: false,
      code: "HANDLER_ERROR",
      error: error instanceof Error ? error.message : String(error),
    };
  } finally {
    if (opensHostCard) {
      agentStore.getState().releaseToolPartOpen(hostToolCallId);
    }
  }
}
